import { QueryClient } from '@tanstack/react-query';
import { resetClientStateForProfileSwitch } from './resetClientStateForProfileSwitch';

/**
 * Auth v2 — multi-tab BroadcastChannel sync (AB#1439).
 *
 * The tab that performs a successful profile switch dispatches
 * `devanza:profile-switched` via {@link resetClientStateForProfileSwitch} on
 * itself AND broadcasts it on this channel for other open tabs. Each listening
 * tab receives the new access token, re-runs the same reset routine, and
 * reloads to the active profile's home.
 *
 * Falls back to the localStorage `storage` event for browsers that don't
 * expose `BroadcastChannel` (older Safari, some embedded webviews).
 */
export const AUTHV2_BROADCAST_CHANNEL = 'devanza:auth-v2';

export interface ProfileSwitchBroadcast {
    readonly type: 'profile-switched';
    readonly activeProfileId: number;
    readonly accessToken: string;
}

let channel: BroadcastChannel | null = null;

/**
 * Post a profile-switch event to other open tabs.
 * Call this from {@link resetClientStateForProfileSwitch} consumers that
 * originate a switch (login / switch-profile / chooser) — NOT from the
 * listener below, which would cause an infinite loop.
 */
export function broadcastProfileSwitch(activeProfileId: number, accessToken: string): void {
    const payload: ProfileSwitchBroadcast = { type: 'profile-switched', activeProfileId, accessToken };
    try {
        if (typeof BroadcastChannel !== 'undefined') {
            if (!channel) channel = new BroadcastChannel(AUTHV2_BROADCAST_CHANNEL);
            channel.postMessage(payload);
            return;
        }
    } catch {
        // fall through to storage fallback
    }
    // Fallback: write a one-shot key in localStorage; other tabs' `storage`
    // listener will fire. The key is cleared immediately so subsequent writes
    // with the same value still trigger an event.
    try {
        localStorage.setItem('devanza:auth-v2:broadcast', JSON.stringify({ ...payload, ts: Date.now() }));
        localStorage.removeItem('devanza:auth-v2:broadcast');
    } catch {
        /* noop */
    }
}

/**
 * Subscribe every other tab to profile-switch broadcasts. Returns a cleanup
 * function. Mounts a single BroadcastChannel listener + a storage-event
 * fallback. Safe to call repeatedly — each call attaches its own pair of
 * listeners so React's StrictMode double-invocation is harmless.
 */
export function subscribeProfileSwitchBroadcast(queryClient: QueryClient): () => void {
    const onPayload = (payload: ProfileSwitchBroadcast) => {
        if (payload?.type !== 'profile-switched') return;
        resetClientStateForProfileSwitch(queryClient, {
            newAccessToken: payload.accessToken,
            activeProfileId: payload.activeProfileId,
            source: 'tab-broadcast',
        });
        // Reload so route guards re-evaluate under the new active profile.
        // No navigation happens in the tab the broadcast came from — only in
        // the peers receiving this event.
        setTimeout(() => { window.location.reload(); }, 50);
    };

    let localChannel: BroadcastChannel | null = null;
    const onStorage = (e: StorageEvent) => {
        if (e.key !== 'devanza:auth-v2:broadcast' || !e.newValue) return;
        try {
            const payload = JSON.parse(e.newValue) as ProfileSwitchBroadcast;
            onPayload(payload);
        } catch {
            /* noop */
        }
    };

    try {
        if (typeof BroadcastChannel !== 'undefined') {
            localChannel = new BroadcastChannel(AUTHV2_BROADCAST_CHANNEL);
            localChannel.onmessage = (msg: MessageEvent) => onPayload(msg.data as ProfileSwitchBroadcast);
        }
    } catch {
        /* noop */
    }

    window.addEventListener('storage', onStorage);

    return () => {
        window.removeEventListener('storage', onStorage);
        if (localChannel) {
            try { localChannel.close(); } catch { /* noop */ }
            localChannel = null;
        }
    };
}
